import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Rocket, Clock, Radio, Layers, CalendarDays, CheckCircle, MessageCircle, GraduationCap } from 'lucide-react';

const WHATSAPP = 'https://wa.me/5561995792667';

const MODULES = [
  { n: '01', t: 'Fundamentos do Gerenciamento BIM', h: '6h' },
  { n: '02', t: 'Estratégia BIM e Implantação', h: '8h' },
  { n: '03', t: 'Gestão da Informação e ISO 19650', h: '8h' },
  { n: '04', t: 'Requisitos de Informação BIM', h: '8h' },
  { n: '05', t: 'Plano de Execução BIM — BEP', h: '9h' },
  { n: '06', t: 'CDE e Fluxos de Informação (OF CDE)', h: '10h' },
  { n: '07', t: 'Gestão e Coordenação do Modelo Federado', h: '10h' },
  { n: '08', t: 'Controle de Qualidade BIM', h: '9h' },
  { n: '09', t: 'Issues, RFI e Não Conformidades', h: '8h' },
  { n: '10', t: 'BIM 4D, 5D e Informação para a Construção', h: '8h' },
  { n: '11', t: 'Indicadores, Dashboards e Tomada de Decisão', h: '6h' },
  { n: '12', t: 'Projeto Integrador — BIM Management do CRAS', h: '8h' },
];

const INFO = [
  { icon: Clock, label: 'Carga horária', value: '100 horas' },
  { icon: Radio, label: 'Formato', value: 'Ao Vivo (Elite)' },
  { icon: Layers, label: 'Projeto-base', value: 'CRAS + OF CDE' },
  { icon: GraduationCap, label: 'Nível', value: 'Intermediário / Avançado' },
];

const WEEKDAY_LABEL = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// Gera 50 aulas de 2h (seg, ter, qua e sex — exceto quinta), a partir de 16/11/2026
const buildSessions = () => {
  const allowed = new Set([1, 2, 3, 5]); // seg, ter, qua, sex
  const sessions = [];
  const cur = new Date(2026, 10, 16); // 16/nov/2026
  while (sessions.length < 50) {
    if (allowed.has(cur.getDay())) sessions.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return sessions;
};

const GROUPED_SESSIONS = (() => {
  const sessions = buildSessions();
  const groups = [];
  let current = null;
  sessions.forEach((d, idx) => {
    const key = `${MONTHS[d.getMonth()]} de ${d.getFullYear()}`;
    if (!current || current.key !== key) {
      current = { key, items: [] };
      groups.push(current);
    }
    current.items.push({
      n: idx + 1,
      day: String(d.getDate()).padStart(2, '0'),
      weekday: WEEKDAY_LABEL[d.getDay()],
    });
  });
  return groups;
})();

const GerenciamentoBIMCard = () => {
  const contact = (msg) => window.open(`${WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank');

  return (
    <Card
      className="relative overflow-hidden border-2 border-amber-500/60 bg-gradient-to-br from-amber-950/40 via-zinc-900 to-black mb-12"
      data-testid="gerenciamento-bim-card"
    >
      {/* Faixa EM LANÇAMENTO */}
      <div className="absolute top-6 -right-14 z-20 rotate-45 bg-amber-500 text-black text-xs font-extrabold uppercase tracking-wider px-16 py-1.5 shadow-lg">
        Em Lançamento
      </div>

      <CardContent className="p-6 md:p-10">
        <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 mb-4 inline-flex items-center gap-1.5">
          <Rocket size={14} /> Nova Formação Profissional
        </Badge>

        <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-2">
          Formação <span className="text-amber-400">Gerenciamento BIM</span>
        </h3>
        <p className="text-amber-200/90 text-base md:text-lg font-medium mb-4">
          Da estratégia à operação de um projeto BIM
        </p>

        <p className="text-gray-300 text-sm md:text-base leading-relaxed max-w-3xl mb-6">
          Você não vai apenas aprender como funciona o gerenciamento BIM.{' '}
          <span className="text-white font-semibold">Você vai gerenciar um projeto BIM real.</span> Ao longo de 100 horas,
          o aluno assume progressivamente o papel de BIM Manager, BIM Coordinator e Information Manager, usando o
          projeto CRAS como laboratório e o OF CDE (OrçaFascio) como ambiente de trabalho, com alinhamento à ISO 19650.
        </p>

        {/* Info rápida */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {INFO.map((i) => {
            const Icon = i.icon;
            return (
              <div key={i.label} className="bg-black/40 border border-amber-500/20 rounded-xl p-3.5">
                <Icon className="text-amber-400 mb-1.5" size={18} />
                <p className="text-[11px] text-gray-400 uppercase tracking-wide">{i.label}</p>
                <p className="text-white text-sm font-semibold">{i.value}</p>
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Módulos */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-bold mb-3 flex items-center gap-2">
              <Layers size={18} className="text-amber-400" /> Estrutura — 12 módulos (Teórico + Prático + Projeto Integrador)
            </h4>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {MODULES.map((m) => (
                <div key={m.n} className="flex items-start gap-2 text-sm text-gray-300 border-b border-white/5 pb-2">
                  <span className="text-amber-400 font-bold text-xs mt-0.5">{m.n}</span>
                  <span className="flex-1">{m.t}</span>
                  <span className="text-gray-500 text-xs whitespace-nowrap">{m.h}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Turma / CTA */}
          <div className="bg-black/50 border border-amber-500/30 rounded-2xl p-5 flex flex-col">
            <div className="flex items-center gap-2 text-amber-300 font-semibold mb-3">
              <CalendarDays size={18} /> Turma ao vivo (Elite)
            </div>
            <ul className="space-y-2 text-sm text-gray-300 mb-4">
              <li className="flex items-start gap-2">
                <CheckCircle size={15} className="text-amber-400 mt-0.5 flex-shrink-0" />
                Início em <span className="text-white font-semibold">16 de novembro de 2026</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={15} className="text-amber-400 mt-0.5 flex-shrink-0" />
                Seg. a sex., das <span className="text-white font-semibold">19h às 21h</span> (exceto quintas)
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={15} className="text-amber-400 mt-0.5 flex-shrink-0" />
                50 aulas de 2h — conclusão em fev/2027
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={15} className="text-amber-400 mt-0.5 flex-shrink-0" />
                Projeto integrador: BIM Management do CRAS
              </li>
            </ul>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
              <p className="text-[11px] text-amber-200/80 uppercase tracking-wide mb-0.5">Investimento — Turma Elite</p>
              <p className="text-3xl font-extrabold text-white leading-none">R$ 5.497</p>
              <p className="text-xs text-gray-300 mt-1">em até 12x no cartão de crédito (juros do cartão)</p>
            </div>

            <div className="mt-auto">
              <div className="mb-3">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-zinc-800 text-gray-300 border border-zinc-700 rounded-full px-3 py-1">
                  EaD Gravado — em breve
                </span>
              </div>
              <Button
                onClick={() => contact('Olá! Tenho interesse na turma ao vivo da Formação Gerenciamento BIM (início 16/11/2026, R$ 5.497 em até 12x).')}
                className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold"
                data-testid="gerenciamento-bim-cta"
              >
                <MessageCircle className="mr-2" size={18} /> Quero garantir minha vaga
              </Button>
              <p className="text-[11px] text-gray-500 text-center mt-2">Vagas limitadas — turma de lançamento</p>
            </div>
          </div>
        </div>

        {/* Calendário das aulas */}
        <div className="mt-8 bg-black/40 border border-amber-500/20 rounded-2xl p-5" data-testid="gerbim-calendar">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h4 className="text-white font-bold flex items-center gap-2">
              <CalendarDays size={18} className="text-amber-400" /> Calendário das aulas
            </h4>
            <span className="text-xs text-gray-400">50 aulas · 2h/dia · 19h–21h · seg, ter, qua e sex</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {GROUPED_SESSIONS.map((g) => (
              <div key={g.key} className="bg-zinc-900/60 border border-white/5 rounded-xl p-3.5">
                <p className="text-amber-300 font-semibold text-sm mb-2 capitalize">{g.key}</p>
                <ul className="space-y-1">
                  {g.items.map((it) => (
                    <li key={it.n} className="flex items-center gap-2 text-xs text-gray-300">
                      <span className="text-amber-400/80 font-mono w-8 flex-shrink-0">#{it.n}</span>
                      <span className="w-6 font-semibold text-white">{it.day}</span>
                      <span className="text-gray-400">{it.weekday}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mt-3">
            Datas previstas para a turma de lançamento. Eventuais ajustes de feriados serão comunicados aos matriculados.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default GerenciamentoBIMCard;
